import * as React from 'react';
import { Route } from 'react-router';
import TrackPurchase from '@reverbdotcom/commons/dist/components/track_purchase';
import CoreClient from '../lib/core_client';
import URLHelpers from '../components/shared/url_helpers';
import isNode from '@reverbdotcom/commons/dist/is-node';
import {
  some,
  sumBy,
} from 'lodash';

import {
  MParticlePageName,
  PageViewEvent,
} from '@reverbdotcom/commons/dist/elog/mparticle_tracker';

interface ICartResponse {
  cart_items: object;
}

const CART_URI = 'cart';
const CHECKOUT_CONFIRMATION_URI = 'multi-checkout/:order_bundle_id/confirmation';
const CHECKOUT_SHIPPING_URI = 'multi-checkout/:checkout_id/:payment_method';
const CHECKOUT_PAYMENT_URI = 'multi-checkout/:checkout_id/:payment_method/payment';
const CHECKOUT_REVIEW_URI = 'multi-checkout/:checkout_id/:payment_method/review';
const CHECKOUT_CHANGE_SHIPPING_URI = 'multi-checkout/:checkout_id/:payment_method/change_shipping';
const CHECKOUT_CHANGE_PAYMENT_URI = 'multi-checkout/:checkout_id/:payment_method/change_payment';
const CHECKOUT_ORDER_BUNDLE_CONFIRMATION_URI = 'multi-checkout/:checkout_id/:payment_method/:order_bundle_id/confirmation';
// we mount the router globally on bundled checkout pages. This catch-all route prevents 404 errors in RRE
// for routes that we don't need to track.
const CHECKOUT_NO_OP_URI = 'multi-checkout/*';
const CART_NO_OP_URI = 'cart/*';

const VALID_URI_PAYMENT_METHODS = Object.freeze([
  'direct',
  'paypal',
  'affirm',
  'partial_payments',
  'vaulted_paypal',
  'sofort',
  'apple_pay',
]);

const PAYMENT_METHOD_FROM_URI = {
  direct: 'direct_checkout',
  paypal: 'paypal',
  affirm: 'affirm',
  vaulted_paypal: 'paypal',
  sofort: 'sofort',
  apple_pay: 'apple_pay',
};

function isTrackableRoute(params): boolean {
  const paymentMethod = params.payment_method;
  return !paymentMethod || VALID_URI_PAYMENT_METHODS.indexOf(paymentMethod) > -1;
}

function cartItemsForPaymentMethod(cartItems, paymentMethod) {
  if (!paymentMethod) return cartItems;
  return cartItems.filter(item => item.payment_methods.hasOwnProperty(paymentMethod));
}

function getTotal(cartItems, key) {
  return sumBy(
    cartItems,
    (item) => {
      return item[key] && parseFloat(item[key].amount) || 0;
    },
  );
}

export function buildCheckoutAttributes(responseCartItems, paymentMethod: string): Partial<PageViewEvent> {
  const cartItems = cartItemsForPaymentMethod(responseCartItems, paymentMethod);
  const itemTotalPrice = getTotal(cartItems, 'item_price');
  const shippingTotalPrice = getTotal(cartItems, 'shipping_price');
  const negotiatedOrder = some(cartItems, { type: 'offer' });

  return {
    itemCount: cartItems.length,
    totalPrice: itemTotalPrice.toFixed(2),
    totalShippingRate: shippingTotalPrice.toFixed(2),
    paymentMethod: paymentMethod.toUpperCase(),
    'negotiatedOrder?': negotiatedOrder,
  };
}

const PAGE_NAMES: Record<string, MParticlePageName> = Object.freeze({
  [CART_URI]: MParticlePageName.Cart,
  [CHECKOUT_SHIPPING_URI]: MParticlePageName.Shipping,
  [CHECKOUT_PAYMENT_URI]: MParticlePageName.Payment,
  [CHECKOUT_REVIEW_URI]: MParticlePageName.ReviewOrder,
  [CHECKOUT_CHANGE_SHIPPING_URI]: MParticlePageName.ChangeShipping,
  [CHECKOUT_CHANGE_PAYMENT_URI]: MParticlePageName.ChangePayment,
});

function buildCheckoutPageView(params, route) {
  if (isNode) { return; }

  const routePath = route.path;
  const pageName = PAGE_NAMES[routePath];
  if (!pageName) return;

  const paymentMethod = PAYMENT_METHOD_FROM_URI[params.payment_method] || '';

  const headers = {
    'X-Display-Currency': 'USD',
  };

  return CoreClient.get<ICartResponse>(URLHelpers.apiCartPath, { headers }).then((response) => {
    return {
      pageName,
      ...buildCheckoutAttributes(response.cart_items, paymentMethod),
    };
  });
}

export function checkoutPageViewAttributes(params, route) {
  if (!isTrackableRoute(params)) return;
  return buildCheckoutPageView(params, route);
}

const CHECKOUT_URIS = Object.freeze([
  CART_URI,
  CHECKOUT_SHIPPING_URI,
  CHECKOUT_PAYMENT_URI,
  CHECKOUT_REVIEW_URI,
  CHECKOUT_CHANGE_SHIPPING_URI,
  CHECKOUT_CHANGE_PAYMENT_URI,
]);

const CONFIRMATION_ROUTES = [
  <Route
    key={CHECKOUT_CONFIRMATION_URI} // also matches view shipping route so needs to come first
    path={CHECKOUT_CONFIRMATION_URI}
    getComponent={(_location, cb) => {
      cb(null, TrackPurchase);
    }}
  />,
  <Route
    key={CHECKOUT_ORDER_BUNDLE_CONFIRMATION_URI} // also matches no-op route so needs to come first
    path={CHECKOUT_ORDER_BUNDLE_CONFIRMATION_URI}
    getComponent={(_location, cb) => {
      cb(null, TrackPurchase);
    }}
  />,
];

const NO_OP_ROUTES = [
  <Route
    key={CHECKOUT_NO_OP_URI}
    path={CHECKOUT_NO_OP_URI}
  />,
  <Route
    key={CART_NO_OP_URI}
    path={CART_NO_OP_URI}
  />,
];

const CHECKOUT_ROUTES = CHECKOUT_URIS.map((route) => {
  return (
    <Route
      key={route}
      path={route}
      props={{
        pageViewEvent: ({ params, innerRoute }) => {
          return checkoutPageViewAttributes(params, innerRoute);
        },
      }}
    />
  );
});

const ALL_CHECKOUT_ROUTES = [
  ...CONFIRMATION_ROUTES,
  ...CHECKOUT_ROUTES,
  ...NO_OP_ROUTES,
];

export default ALL_CHECKOUT_ROUTES;



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/routes/checkout.tsx